import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  MoreHorizontal,
  MapPin,
  Link as LinkIcon,
  Lock,
  Loader2,
  BookOpen,
  Edit3,
} from "lucide-react";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import PostCard from "@/components/PostCard";
import PostThumb from "@/components/PostThumb";
import UserAvatar from "@/components/UserAvatar";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/useSeo";
import { resolveAvatarUrl } from "@/lib/media";
import { toast } from "sonner";

/**
 * UserProfile — 1:1 web port of iOS `src/app/(app)/u/[username].tsx`.
 *
 * Public profile for any user by `:username`:
 *   • Header with back arrow + "..." menu (Report user / Block user)
 *   • Avatar with rainbow ring, cursive display name, verified badge,
 *     "Private" lock icon, bio, MapPin location, brand website chip
 *   • 3-stat row (Posts / Followers / Following) — Followers/Following tap
 *     through to `/u/:username/followers` (etc.)
 *   • Action area: Follow / Following+Unfollow / Requested+Revoke
 *   • Posts (3-per-row grid) + Opinion (PostCard list) tabs
 *   • Private + not-following = tabs hidden with "This account is private"
 *
 * If the viewed username is the current user's, we redirect to `/profile`
 * so the user always sees the owned-profile experience with Edit / Log out.
 */

const RAINBOW =
  "linear-gradient(90deg, #FFD600, #FF7A00, #FF0069, #D300C5, #7638FA)";
// Brand purple via CSS variable so it auto-switches for dark mode
// (`#7638FA` light → `#9B6BFF` dark) — matches iOS `theme.brand`.
const BRAND = "hsl(var(--brand))";
const VERIFIED = "#3D9CFF";

function StatButton({ label, value, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center px-3 active:opacity-70 transition-opacity"
      data-testid={testId}
    >
      <span
        className="text-foreground font-extrabold tabular-nums"
        style={{ fontSize: 19, lineHeight: "24px" }}
      >
        {typeof value === "number" ? value.toLocaleString() : value ?? "0"}
      </span>
      <span className="text-muted-foreground text-[12px] mt-0.5">{label}</span>
    </button>
  );
}

function PostThumbCell({ post, onClick, testId }) {
  return (
    <div
      className="relative overflow-hidden rounded-lg"
      style={{ width: "32.5%", aspectRatio: "1 / 1" }}
    >
      <PostThumb post={post} onClick={onClick} testId={testId} />
    </div>
  );
}

function MoreMenu({ open, onClose, onReport, onBlock }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="user-more-menu"
    >
      <div
        className="w-full sm:max-w-[320px] bg-card rounded-t-[22px] sm:rounded-[22px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onReport}
          className="w-full h-14 px-5 flex items-center gap-3 text-left hover:bg-secondary transition-colors border-b border-border"
          data-testid="user-menu-report"
        >
          <span className="text-foreground text-[15px] font-semibold">Report user</span>
        </button>
        <button
          type="button"
          onClick={onBlock}
          className="w-full h-14 px-5 flex items-center gap-3 text-left hover:bg-secondary transition-colors"
          data-testid="user-menu-block"
        >
          <span className="text-destructive text-[15px] font-semibold">Block user</span>
        </button>
        <div className="border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="w-full h-14 flex items-center justify-center text-foreground font-bold text-[15px] hover:bg-secondary transition-colors"
            data-testid="user-menu-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UserProfile() {
  const navigate = useNavigate();
  const { username: routeUsername } = useParams();
  const { user: currentUser } = useAuth();
  const isOwn = !!currentUser && routeUsername === currentUser.username;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [followState, setFollowState] = useState({ following: false, pending: false });
  const [followLoading, setFollowLoading] = useState(false);
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [opinionPosts, setOpinionPosts] = useState(null);
  const [postsLoading, setPostsLoading] = useState(true);
  const [opinionLoading, setOpinionLoading] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Redirect to own /profile if viewing self
  useEffect(() => {
    if (isOwn) navigate("/profile", { replace: true });
  }, [isOwn, navigate]);

  // Per-profile SEO / Open Graph tags.
  //
  // Format follows Instagram's convention which Google/social share
  // cards handle really well:
  //   "Display Name (@username) • BrontoY photos and views"
  //
  // A photos-and-views phrasing (instead of Instagram's "photos and
  // videos") matches BrontoY's mixed feed of images + Opinion posts
  // and repeats the "Share Moments. Express Views." brand line the
  // Landing hero uses.
  const uname = profile?.username || routeUsername;
  const dname = profile?.display_name;
  const seoTitle = dname
    ? `${dname} (@${uname}) — BrontoY`
    : `@${uname} — BrontoY`;
  const seoDescription = (() => {
    const bio = (profile?.bio || "").trim();
    if (bio) {
      const clean = bio.replace(/\s+/g, " ");
      return clean.length > 200 ? `${clean.slice(0, 197)}…` : clean;
    }
    if (dname) {
      return `${dname} (@${uname}) on BrontoY — share moments and express views.`;
    }
    return `@${uname} on BrontoY — share moments and express views.`;
  })();
  const seoImage = resolveAvatarUrl(profile) || undefined;
  useSeo({
    title: seoTitle,
    description: seoDescription,
    canonicalPath: `/${uname}`,
    image: seoImage,
    ogType: "profile",
  });

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setErr("");

    // Guest path — no token → use the public endpoint. Returns the
    // full profile + up to 3 latest standard_posts + 3 opinion_posts
    // for `account_privacy=public` accounts. Private accounts return
    // the profile with empty post arrays; the UI shows a
    // "private-account" prompt instead of the grid.
    if (!currentUser) {
      try {
        const res = await api.get(EP.publicProfileByUsername(routeUsername));
        let p = unwrap(res.data);
        if (Array.isArray(p)) p = p[0];
        if (!p || typeof p !== "object" || !p.username) {
          throw new Error("Profile not found.");
        }
        setProfile(p);
        setFollowState({ following: false, pending: false });
        // Seed the guest post grid immediately from the inline arrays —
        // guests aren't allowed to paginate, so this IS the grid.
        const std = Array.isArray(p.standard_posts) ? p.standard_posts : [];
        const opi = Array.isArray(p.opinion_posts) ? p.opinion_posts : [];
        setPosts(std.slice(0, 3));
        setOpinionPosts(opi.slice(0, 3));
      } catch (e) {
        setErr(apiError(e) || "Profile not found.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Authed path — matches iOS spec 15_OTHER_USER.md exactly.
    let primaryErr = null;
    try {
      const res = await api.get(EP.profileByUsername(routeUsername));
      let p = unwrap(res.data);
      p = p?.profile || p?.user || p;
      if (Array.isArray(p)) p = p[0];
      if (!p || typeof p !== "object" || (!p.username && !p.user_id && !p.id)) {
        throw new Error("Profile not found.");
      }
      setProfile(p);
      setFollowState({
        following: !!(p?.viewer_is_following || p?.is_following),
        pending: !!(p?.viewer_follow_pending || p?.follow_pending),
      });
      setLoading(false);
      return;
    } catch (e) {
      primaryErr = e;
      console.warn(
        "[profile] by_username failed for",
        routeUsername,
        (e?.response?.data && JSON.stringify(e.response.data).slice(0, 300)) ||
          e?.message
      );
    }

    // Fallback: /search/users, then /users/{id} to enrich.
    try {
      const sres = await api.get(EP.searchUsers(routeUsername, 1, 10));
      const sd = unwrap(sres.data);
      const list = Array.isArray(sd)
        ? sd
        : sd?.items || sd?.users || sd?.results || sd?.data || [];
      const hit = list.find(
        (u) => String(u?.username || "").toLowerCase() === routeUsername.toLowerCase()
      );
      if (!hit) throw primaryErr || new Error("User not found.");
      let enriched = hit;
      const uid = hit?.user_id ?? hit?.id;
      if (uid != null) {
        try {
          const ures = await api.get(EP.userById(uid));
          let u = unwrap(ures.data);
          u = u?.user || u?.profile || u;
          if (Array.isArray(u)) u = u[0];
          if (u && typeof u === "object") enriched = { ...hit, ...u };
        } catch {
          /* users endpoint unavailable — search payload is enough to render */
        }
      }
      setProfile(enriched);
      setFollowState({
        following: !!(enriched?.viewer_is_following || enriched?.is_following),
        pending: !!(enriched?.viewer_follow_pending || enriched?.follow_pending),
      });
    } catch {
      setErr(apiError(primaryErr) || "User not found.");
    } finally {
      setLoading(false);
    }
  }, [routeUsername, currentUser]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const uid = profile?.user_id ?? profile?.id;
  const isPrivate = profile?.account_privacy === "private";
  const canSeeContent = !isPrivate || followState.following;

  // Authoritative cross-check for the follow button — some Xano payloads
  // ship without `viewer_is_following` (or with a stale `false`), which
  // left the button reading "Follow" even after the viewer had just
  // followed the target.  Scan up to 5 pages of `/me/following` and flip
  // the button to "Following" on a hit.  Skipped while a request is
  // pending or if the viewer just unfollowed in this session.
  const unfollowedInSessionRef = useRef(false);
  useEffect(() => {
    if (!uid || isOwn) return undefined;
    if (followState.following || followState.pending) return undefined;
    let cancelled = false;
    (async () => {
      for (let page = 1; page <= 5; page += 1) {
        try {
          const res = await api.get(EP.myFollowing(page, 50));
          const d = unwrap(res.data);
          const list = Array.isArray(d) ? d : d?.items || d?.users || d?.data || [];
          if (!list || list.length === 0) return;
          const hit = list.some((u) => {
            const rid = u?.user_id ?? u?.id ?? u?.followee_id;
            return String(rid) === String(uid);
          });
          if (hit) {
            if (!cancelled && !unfollowedInSessionRef.current) {
              setFollowState((s) => (s.pending ? s : { ...s, following: true }));
            }
            return;
          }
          if (list.length < 50) return;
        } catch {
          return;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, isOwn, followState.following, followState.pending]);

  // Load Posts
  useEffect(() => {
    if (!profile || !canSeeContent) {
      setPostsLoading(false);
      return;
    }
    // Guest path — posts were already seeded from the public
    // /by_username response (max 3 latest). No paginated fetch for
    // guests: those endpoints require auth and would just 401.
    if (!currentUser) {
      setPostsLoading(false);
      return;
    }
    setPostsLoading(true);
    (async () => {
      let list = [];
      if (uid != null) {
        try {
          const res = await api.get(EP.profileStandardPosts(uid, 1, 30));
          const d = unwrap(res.data);
          list = Array.isArray(d) ? d : d?.items || d?.posts || d?.data || [];
        } catch {
          list = [];
        }
      }
      if (list.length === 0) {
        try {
          const res = await api.get(EP.profilePostsByUsername(routeUsername, 1, 30));
          const d = unwrap(res.data);
          const all = Array.isArray(d) ? d : d?.items || d?.posts || [];
          list = all.filter(
            (p) => (p.Post_type || p.post_type || "Standard").toLowerCase() !== "opinion"
          );
        } catch {
          /* empty */
        }
      }
      setPosts(list);
      setPostsLoading(false);
    })();
  }, [profile, uid, routeUsername, canSeeContent]);

  // Lazy Opinion tab
  useEffect(() => {
    if (tab !== "opinion" || opinionPosts !== null || !canSeeContent) return;
    // Guest path — opinion posts were seeded during the public
    // /by_username load; don't hit the authed endpoints.
    if (!currentUser) return;
    setOpinionLoading(true);
    (async () => {
      let list = [];
      if (uid != null) {
        try {
          const res = await api.get(EP.profileOpinionPosts(uid, 1, 30));
          const d = unwrap(res.data);
          list = Array.isArray(d) ? d : d?.items || d?.posts || d?.data || [];
        } catch {
          list = [];
        }
      }
      if (list.length === 0 && routeUsername) {
        try {
          const res = await api.get(EP.profilePostsByUsername(routeUsername, 1, 30));
          const d = unwrap(res.data);
          const all = Array.isArray(d) ? d : d?.items || d?.posts || [];
          list = all.filter(
            (p) => (p.Post_type || p.post_type || "").toLowerCase() === "opinion"
          );
        } catch {
          /* empty */
        }
      }
      setOpinionPosts(list);
      setOpinionLoading(false);
    })();
  }, [tab, uid, routeUsername, opinionPosts, canSeeContent]);

  const doFollow = async () => {
    if (!uid || followLoading) return;
    setFollowLoading(true);
    const wasFollowing = followState.following;
    const wasPending = followState.pending;
    const username = profile?.username;
    try {
      if (wasFollowing) {
        // Unfollow — DELETE /users/{id}/follow.  Mark the ref so the
        // /me/following cross-check effect doesn't flip us straight
        // back to "Following".
        unfollowedInSessionRef.current = true;
        setFollowState({ following: false, pending: false });
        await api.delete(EP.unfollowUser(uid));
      } else if (wasPending) {
        // Revoke a pending follow request — POST /me/follow_requests/revoke
        // with { user_id, username } identifying the TARGET (this user).
        setFollowState({ following: false, pending: false });
        await api.post(EP.revokeFollowRequest, { user_id: uid, username });
        toast.success("Request cancelled");
      } else if (isPrivate) {
        // Private account → dedicated follow-request route.
        unfollowedInSessionRef.current = false;
        setFollowState({ following: false, pending: true });
        await api.post(EP.sendFollowRequest, { target_user_id: uid, username });
        toast.success("Follow request sent");
      } else {
        // Public follow — POST /follow_by_username { user_id, username }.
        // Fall back to POST /follow/{id} if username is missing.
        unfollowedInSessionRef.current = false;
        setFollowState({ following: true, pending: false });
        if (username) {
          await api.post(EP.followByUsername, { user_id: uid, username });
        } else {
          await api.post(EP.followUser(uid), {});
        }
      }
    } catch (e) {
      // Rollback
      setFollowState({ following: wasFollowing, pending: wasPending });
      toast.error(apiError(e));
    } finally {
      setFollowLoading(false);
    }
  };

  const doBlock = async () => {
    if (!uid) return;
    setMoreOpen(false);
    if (!window.confirm(`Block @${profile.username}? They won't be able to see your profile or content.`)) return;
    try {
      // iOS spec: POST /block/{user_id} REQUIRES { username } body.
      await api.post(EP.blockUser(uid), { username: profile.username });
      toast.success("User blocked");
      navigate(-1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const back = () =>
    window.history.length > 1 ? navigate(-1) : navigate("/home", { replace: true });

  if (loading) {
    return (
      <div
        className="min-h-[100dvh] flex items-center justify-center bg-background"
        data-testid="user-profile-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: BRAND }} />
      </div>
    );
  }

  if (err || !profile) {
    // Guests hitting a valid profile URL should NOT see a raw
    // "Unauthorized - Authentication Required" screen. Instead we
    // render a lightweight public preview keyed off the URL slug so
    // there's SOMETHING crawlers (and humans) can see, then let
    // PublicProfileGate's 10-s popup nudge them to sign in.
    const isGuest = !currentUser;
    if (isGuest) {
      const seedInitial = (uname || "?").slice(0, 2).toUpperCase();
      return (
        <div
          className="min-h-[100dvh] w-full bg-background text-foreground"
          data-testid="user-profile-guest-preview"
        >
          <div className="max-w-[600px] mx-auto px-6 py-10 sm:py-16 text-center">
            {/* Placeholder avatar — deterministic initials so shared
                links still look intentional before real data loads. */}
            <div
              className="w-24 h-24 sm:w-28 sm:h-28 mx-auto rounded-full flex items-center justify-center text-2xl font-semibold text-white/90 mb-5"
              style={{
                background: RAINBOW,
              }}
              aria-hidden="true"
            >
              {seedInitial}
            </div>
            {/*
              H1 uses the @handle as the page's primary heading — this
              is what Google & social crawlers index as the "topic" of
              the URL. Instagram / X / Threads all follow the same
              pattern (@handle as the page title/H1).
            */}
            <h1
              className="font-display text-3xl sm:text-4xl font-medium tracking-tighter"
              data-testid="user-profile-h1-guest"
            >
              @{uname}
            </h1>
            <p className="text-sm text-muted-foreground mt-3 max-w-sm mx-auto">
              Sign in to see @{uname}&apos;s profile, posts and views on BrontoY.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-[100dvh] p-6 bg-background" data-testid="user-profile-error">
        <button
          type="button"
          onClick={back}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary"
          aria-label="Back"
        >
          <ChevronLeft size={25} strokeWidth={2.4} />
        </button>
        <p className="text-muted-foreground text-center mt-10">
          {err || "User not found."}
        </p>
      </div>
    );
  }

  const displayName = profile.display_name || profile.username;
  const followersVal = profile.followers_count ?? profile.follower_count ?? 0;
  const followingVal = profile.following_count ?? 0;
  const postsVal = profile.posts_count ?? posts.length;
  const bio = profile.bio;
  const location = profile.location;
  const website = profile.website;

  return (
    <div
      className="min-h-[100dvh] w-full bg-background text-foreground flex flex-col"
      data-testid="user-profile-screen"
    >
      {/* Header */}
      <div
        className="h-[52px] px-2 flex items-center justify-between border-b border-border shrink-0"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <button
          type="button"
          onClick={back}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
          aria-label="Back"
          data-testid="user-profile-back"
        >
          <ChevronLeft size={25} strokeWidth={2.4} />
        </button>
        <h1
          className="text-foreground font-jakarta font-bold text-[17px]"
          style={{ letterSpacing: "-0.3px" }}
          data-testid="user-profile-handle"
        >
          @{profile.username}
        </h1>
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
          aria-label="More"
          data-testid="user-profile-more"
        >
          <MoreHorizontal size={22} className="text-foreground" />
        </button>
      </div>

      <div className="w-full md:max-w-none max-w-[560px] mx-auto md:mx-0">
        {/* Header — iOS spec 15: avatar LEFT (82px) + 3 stats RIGHT
            in a horizontal row.  Cursive display name + @username +
            bio + location/website below. */}
        <div className="px-4 pt-4">
          <div className="flex items-start gap-4">
            <div className="rounded-full p-[3px] shrink-0" style={{ background: RAINBOW }}>
              <div className="rounded-full bg-background p-[3px]">
                <UserAvatar user={profile} size="xl" />
              </div>
            </div>
            <div className="flex-1 flex justify-around pt-3">
              <StatButton label="Posts" value={postsVal} onClick={() => setTab("posts")} testId="user-stat-posts" />
              <StatButton
                label="Followers"
                value={followersVal}
                onClick={() => navigate(`/${routeUsername}/followers`)}
                testId="user-stat-followers"
              />
              <StatButton
                label="Following"
                value={followingVal}
                onClick={() => navigate(`/${routeUsername}/following`)}
                testId="user-stat-following"
              />
            </div>
          </div>

          <div className="mt-3.5 flex items-center gap-1.5 flex-wrap">
            <h2
              className="font-grand-hotel text-foreground"
              style={{ fontSize: 26, lineHeight: 1.15 }}
              data-testid="profile-display-name"
            >
              {displayName}
            </h2>
            {profile.is_verified && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill={VERIFIED}>
                <path
                  fill={VERIFIED}
                  d="M12 2c1.9 1.5 4.4 2.3 7 2v5c0 5.55-3.84 10.74-9 12-5.16-1.26-9-6.45-9-12V4c2.6.3 5.1-.5 7-2h4z"
                />
                <path
                  fill="#fff"
                  d="M10.5 15l-3-3 1.4-1.4 1.6 1.6 4.6-4.6 1.4 1.4z"
                />
              </svg>
            )}
            {isPrivate && (
              <span className="inline-flex items-center gap-1 border border-border rounded-full px-2 py-0.5 text-[11px] text-muted-foreground">
                <Lock size={11} />
                Private
              </span>
            )}
          </div>
          <div className="text-muted-foreground text-[14px] mt-0.5">@{profile.username}</div>

          {bio && (
            <p className="text-foreground text-[15px] mt-2.5 leading-[21px]">{bio}</p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5">
            {location && (
              <span className="inline-flex items-center gap-1 text-muted-foreground text-[13.5px]">
                <MapPin size={14} />
                {location}
              </span>
            )}
            {website && (
              <a
                href={website.startsWith("http") ? website : `https://${website}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[13.5px] font-semibold hover:opacity-80"
                style={{ color: BRAND }}
              >
                <LinkIcon size={14} />
                {website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </a>
            )}
          </div>

          {/* Follow / Unfollow action area — iOS spec 15:
              - Not following            → single "Follow" filled button
              - Following                → stacked pair: "Following" secondary + red "Unfollow" outline below
              - Pending (private target) → stacked pair: "Requested" secondary disabled + red "Revoke request" outline below */}
          <div className="mt-4">
            {followState.following ? (
              <div className="flex flex-col gap-2.5">
                <button
                  type="button"
                  disabled
                  className="w-full h-11 rounded-xl font-bold text-[14px] bg-secondary text-foreground opacity-95"
                  data-testid="user-following-status"
                >
                  Following
                </button>
                <button
                  type="button"
                  onClick={doFollow}
                  disabled={followLoading}
                  className="w-full h-[46px] rounded-xl border-[1.5px] font-extrabold text-[15px] flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                  style={{
                    borderColor: "#FF3B30",
                    color: "#FF3B30",
                    backgroundColor: "rgba(255,59,48,0.07)",
                  }}
                  data-testid="user-unfollow-btn"
                >
                  {followLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unfollow"}
                </button>
              </div>
            ) : followState.pending ? (
              <div className="flex flex-col gap-2.5">
                <button
                  type="button"
                  disabled
                  className="w-full h-11 rounded-xl font-bold text-[14px] bg-secondary text-foreground opacity-70"
                  data-testid="user-requested-status"
                >
                  Requested
                </button>
                <button
                  type="button"
                  onClick={doFollow}
                  disabled={followLoading}
                  className="w-full h-[46px] rounded-xl border-[1.5px] font-extrabold text-[15px] flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                  style={{
                    borderColor: "#FF3B30",
                    color: "#FF3B30",
                    backgroundColor: "rgba(255,59,48,0.07)",
                  }}
                  data-testid="user-revoke-request-btn"
                >
                  {followLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Revoke request"}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={doFollow}
                disabled={followLoading}
                className="w-full h-11 rounded-xl font-bold text-[14px] text-white flex items-center justify-center gap-2 transition-all active:opacity-85 disabled:opacity-60"
                style={{ background: BRAND }}
                data-testid="user-follow-btn"
              >
                {followLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Follow"}
              </button>
            )}
          </div>
        </div>

        {/* Private wall */}
        {!canSeeContent ? (
          <div className="mt-10 pb-10 flex flex-col items-center gap-2 text-center px-6" data-testid="user-private-wall">
            <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center">
              <Lock size={20} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground font-bold text-[15px] mt-1">
              This account is private.
            </h3>
            <p className="text-muted-foreground text-[13px] max-w-[260px]">
              Follow @{profile.username} to see their posts and opinions.
            </p>
          </div>
        ) : (
          <>
            {/* Content tabs */}
            <div className="mt-6 border-t border-border">
              <div className="flex">
                <button
                  type="button"
                  onClick={() => setTab("posts")}
                  className="flex-1 h-11 flex items-center justify-center relative"
                  data-testid="user-tab-posts"
                >
                  <span
                    className={`text-[13px] font-extrabold uppercase tracking-wide ${
                      tab === "posts" ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    Posts
                  </span>
                  {tab === "posts" && (
                    <span
                      className="absolute bottom-0 left-1/2 -translate-x-1/2"
                      style={{ width: 28, height: 3, borderRadius: 1.5, background: RAINBOW }}
                    />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setTab("opinion")}
                  className="flex-1 h-11 flex items-center justify-center relative"
                  data-testid="user-tab-opinion"
                >
                  <span
                    className={`text-[13px] font-extrabold uppercase tracking-wide flex items-center gap-1.5 ${
                      tab === "opinion" ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    <BookOpen size={13} strokeWidth={2} />
                    Opinion
                  </span>
                  {tab === "opinion" && (
                    <span
                      className="absolute bottom-0 left-1/2 -translate-x-1/2"
                      style={{ width: 36, height: 3, borderRadius: 1.5, background: RAINBOW }}
                    />
                  )}
                </button>
              </div>

              {tab === "posts" ? (
                postsLoading ? (
                  <div className="py-14 flex justify-center">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: BRAND }} />
                  </div>
                ) : posts.length === 0 ? (
                  <div className="py-14 flex flex-col items-center gap-2 text-center">
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                      <Edit3 size={20} className="text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground text-[13px]">No posts yet.</p>
                  </div>
                ) : (
                  <div className="flex flex-wrap px-3 gap-[3px]">
                    {posts.map((p) => (
                      <PostThumbCell
                        key={String(p.id ?? p.post_id)}
                        post={p}
                        onClick={() => navigate(`/post/${p.id ?? p.post_id}`)}
                        testId={`user-post-thumb-${p.id ?? p.post_id}`}
                      />
                    ))}
                  </div>
                )
              ) : opinionLoading ? (
                <div className="py-14 flex justify-center">
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: BRAND }} />
                </div>
              ) : !opinionPosts || opinionPosts.length === 0 ? (
                <div className="py-14 flex flex-col items-center gap-2 text-center">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                    <BookOpen size={20} className="text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground text-[13px]">No opinions yet.</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {opinionPosts.map((p) => (
                    <PostCard key={String(p.id ?? p.post_id)} post={p} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <MoreMenu
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onReport={() => {
          setMoreOpen(false);
          toast.info("Report screen coming soon.");
        }}
        onBlock={doBlock}
      />
    </div>
  );
}
